import {
  Claim, CommissionLine, Installment, LineOfBusiness, Policy, ProtoDb, Quote, Ticket, TreeMember,
} from '../models/domain.models';
import { LINE_LABELS, periodLabel } from '../models/labels';
import {
  CLAIM_DEADLINE_WARN_DAYS, CLAIM_STALE_NEWS_DAYS, COMMISSION_TOLERANCE, MORA_DAYS, SYNC_STALE_DAYS,
} from '../models/rules';
import {
  AttentionItem, BarGroup, BarItem, BoardId, BoardKpi, BoardTable, BoardView, Coverage, DashboardView, DateRange, FunnelStage,
  LinkTarget, Metric, ReportScope,
} from '../models/views.models';
import { lineExpected, subtreeOids } from './commission-math';
import { isUncontactedChoice, QUOTE_STAGES } from './row-projections';
import { addDays, daysBetween } from './rng';
import { StoreHost } from './store-host';

export interface ReportFilters {
  period?: string;
  from?: string;
  to?: string;
  memberOid?: string;
  companyOid?: string;
  line?: string;
  asOf?: string;
}

interface Ctx {
  today: string;
  role: 'producer' | 'seller';
  me: TreeMember;
  range: DateRange;
  previous: DateRange;
  complete: boolean;
  allOwners: Set<string>;
  owners: Set<string>;
  companyOid: string;
  line: string;
  memberOid: string;
  filters: ReportFilters;
}

const OPEN_CLAIM = ['reported', 'documentation', 'in-analysis'];
const ACTIVE_TICKET = ['open', 'in-progress', 'waiting-company'];
const PERIOD_SPAN: Record<string, number> = { current: 1, previous: 1, quarter: 3, year: 12 };
const MONTHS_IN_TREND = 6;
const TOP_ITEMS = 8;
const NO_REASON = 'Sin motivo cargado';

const fmtDate = (iso: string) => iso.split('-').reverse().join('/');
const sum = <T>(items: T[], pick: (item: T) => number) => items.reduce((total, item) => total + pick(item), 0);
const pct = (part: number, whole: number) => (whole === 0 ? 0 : Math.round((part / whole) * 1000) / 10);

function monthStart(iso: string): string {
  return `${iso.slice(0, 8)}01`;
}

function shiftMonth(iso: string, months: number): string {
  const total = Number(iso.slice(0, 4)) * 12 + (Number(iso.slice(5, 7)) - 1) + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

function monthEnd(iso: string): string {
  return addDays(shiftMonth(iso, 1), -1);
}

function bars(map: Map<string, { label: string; value: number; sub?: string | null; link?: LinkTarget | null }>, limit = 0): BarItem[] {
  const items = [...map.entries()]
    .map(([key, v]) => ({ key, label: v.label, value: v.value, sub: v.sub ?? null, link: v.link ?? null }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  return limit > 0 ? items.slice(0, limit) : items;
}

function coverage(total: number, actionable: number, unrecoverable: number, unit: string, actionableLabel: string, unrecoverableLabel: string): Coverage {
  return { covered: total - actionable - unrecoverable, total, unit, actionable, actionableLabel, unrecoverable, unrecoverableLabel };
}

const fullCoverage = (total: number, unit: string): Coverage => coverage(total, 0, 0, unit, '', '');

/** Dashboard (P-01) and boards (P-75): every figure is computed from the store collections the list screens read (D-031-fe). */
export class ReportEngine {
  constructor(private readonly host: StoreHost) {}

  private get db(): ProtoDb {
    return this.host.db;
  }

  // ── Context ───────────────────────────────────────────────────

  private context(f: ReportFilters): Ctx | null {
    const user = this.host.user();
    const me = this.db.members.find(m => m.oid === user?.memberOid);
    if (!me || (user?.role !== 'producer' && user?.role !== 'seller')) return null;
    const allOwners = user.role === 'seller' ? new Set([me.oid]) : subtreeOids(this.db.members, me.oid);
    const memberOid = user.role === 'producer' && f.memberOid && allOwners.has(f.memberOid) && f.memberOid !== me.oid ? f.memberOid : '';
    const today = this.host.today();
    const { range, previous, complete } = this.resolveRange(f, today);
    return {
      today, role: user.role, me, range, previous, complete, allOwners,
      owners: memberOid ? subtreeOids(this.db.members, memberOid) : allOwners,
      companyOid: this.db.companies.some(c => c.oid === f.companyOid) ? f.companyOid ?? '' : '',
      line: f.line && f.line in LINE_LABELS ? f.line : '',
      memberOid, filters: f,
    };
  }

  private scope(ctx: Ctx): ReportScope {
    const members = ctx.role === 'producer'
      ? this.db.members.filter(m => ctx.allOwners.has(m.oid) && m.oid !== ctx.me.oid)
        .sort((a, b) => a.name.localeCompare(b.name)).map(m => ({ oid: m.oid, name: m.name }))
      : [];
    return { role: ctx.role, members, today: ctx.today };
  }

  private resolveRange(f: ReportFilters, today: string): { range: DateRange; previous: DateRange; complete: boolean } {
    const kind = f.period && (f.period in PERIOD_SPAN || f.period === 'custom') ? f.period : 'current';
    const year = Number(today.slice(0, 4));
    let from: string;
    let to: string;
    let prevFrom: string;
    let prevFull: string;
    if (kind === 'custom' && f.from && f.to && f.from <= f.to) {
      from = f.from;
      to = f.to;
      const length = daysBetween(from, to) + 1;
      prevFrom = addDays(from, -length);
      prevFull = addDays(from, -1);
    } else {
      const span = PERIOD_SPAN[kind] ?? 1;
      const base = monthStart(today);
      if (kind === 'previous') from = shiftMonth(base, -1);
      else if (kind === 'quarter') from = shiftMonth(base, -((Number(today.slice(5, 7)) - 1) % 3));
      else if (kind === 'year') from = `${year}-01-01`;
      else from = base;
      to = monthEnd(shiftMonth(from, span - 1));
      prevFrom = shiftMonth(from, -span);
      prevFull = monthEnd(shiftMonth(prevFrom, span - 1));
    }
    const complete = to <= today;
    const elapsed = daysBetween(from, complete ? to : today) + 1;
    const prevTo = complete ? prevFull : [addDays(prevFrom, elapsed - 1), prevFull].sort()[0];
    const label = (a: string, b: string, partialTo: string | null) => {
      if (kind !== 'custom' && a.slice(5) === '01-01' && b.slice(5) === '12-31') return `${a.slice(0, 4)}`;
      if (kind === 'quarter') return `trimestre ${Math.floor((Number(a.slice(5, 7)) - 1) / 3) + 1}.º de ${a.slice(0, 4)}`;
      if (kind !== 'custom' && a.slice(0, 7) === b.slice(0, 7)) {
        return `${periodLabel(a.slice(0, 7))}${partialTo ? ` (al ${fmtDate(partialTo)})` : ''}`;
      }
      return `${fmtDate(a)} – ${fmtDate(b)}`;
    };
    return {
      range: { from, to, label: label(from, to, complete ? null : today) },
      previous: { from: prevFrom, to: prevTo, label: label(prevFrom, prevTo, null) + (complete ? '' : ` (mismo lapso)`) },
      complete,
    };
  }

  private link(path: string, ctx: Ctx, opts: { company?: boolean; line?: 'lineOfBusiness' | 'line' | null; member?: boolean; range?: boolean }, extra: Record<string, string> = {}): LinkTarget {
    const filters: Record<string, string> = {};
    if (opts.range) {
      filters['from'] = ctx.range.from;
      filters['to'] = ctx.range.to;
    }
    if (opts.company !== false && ctx.companyOid) filters['companyOid'] = ctx.companyOid;
    if (opts.line !== null && ctx.line) filters[opts.line ?? 'lineOfBusiness'] = ctx.line;
    if (opts.member !== false && ctx.memberOid) filters['producerOid'] = ctx.memberOid;
    return { path, filters: { ...filters, ...extra } };
  }

  // ── Scoped selections ─────────────────────────────────────────

  private policies(ctx: Ctx): Policy[] {
    return this.db.policies.filter(p => ctx.owners.has(p.ownerOid)
      && (!ctx.companyOid || p.companyOid === ctx.companyOid) && (!ctx.line || p.lineOfBusiness === ctx.line));
  }

  private quotes(ctx: Ctx): Quote[] {
    return this.db.quotes.filter(q => ctx.owners.has(q.ownerOid) && (!ctx.line || q.lineOfBusiness === ctx.line)
      && (!ctx.companyOid || q.offers.some(o => o.companyOid === ctx.companyOid)));
  }

  private claims(ctx: Ctx): Claim[] {
    const lineOf = new Map(this.db.policies.map(p => [p.oid, p.lineOfBusiness]));
    return this.db.claims.filter(c => ctx.owners.has(c.ownerOid) && (!ctx.companyOid || c.companyOid === ctx.companyOid)
      && (!ctx.line || lineOf.get(c.policyOid) === ctx.line));
  }

  private installments(ctx: Ctx): { item: Installment; policy: Policy }[] {
    const byOid = new Map(this.db.policies.map(p => [p.oid, p]));
    return this.db.installments.flatMap(item => {
      const policy = byOid.get(item.policyOid);
      const keep = policy && ctx.owners.has(item.ownerOid) && (!ctx.companyOid || policy.companyOid === ctx.companyOid)
        && (!ctx.line || policy.lineOfBusiness === ctx.line);
      return keep ? [{ item, policy }] : [];
    });
  }

  private lines(ctx: Ctx, inRange: boolean): CommissionLine[] {
    return this.db.commissionLines.filter(l => ctx.owners.has(l.ownerOid)
      && (!ctx.companyOid || l.companyOid === ctx.companyOid) && (!ctx.line || l.lineOfBusiness === ctx.line)
      && (!inRange || (l.paidDate >= ctx.range.from && l.paidDate <= ctx.range.to)));
  }

  private displayStatus(i: Installment, today: string): 'paid' | 'pending' | 'overdue' | 'mora' {
    if (i.status === 'overdue' && daysBetween(i.dueDate, today) > MORA_DAYS) return 'mora';
    return i.status;
  }

  private inRange(date: string, range: DateRange): boolean {
    return date >= range.from && date <= range.to;
  }

  // ── Coverage ──────────────────────────────────────────────────

  private companyOf(oid: string) {
    return this.db.companies.find(c => c.oid === oid);
  }

  private portfolioCoverage(policies: Policy[], today: string): Coverage {
    const active = policies.filter(p => p.status === 'active');
    const stale = active.filter(p => !p.divergent && daysBetween(p.lastSyncDate, today) > this.staleDays(p.lineOfBusiness)).length;
    const divergent = active.filter(p => p.divergent).length;
    return coverage(active.length, stale, divergent, 'pólizas vigentes', 'con sincronización vieja', 'divergentes: la compañía informa otro estado');
  }

  private staleDays(line: LineOfBusiness): number {
    return SYNC_STALE_DAYS[line];
  }

  private collectionsCoverage(policies: Policy[]): Coverage {
    const active = policies.filter(p => p.status === 'active');
    const missing = active.filter(p => !p.installmentsLoaded);
    const unrecoverable = missing.filter(p => this.companyOf(p.companyOid)?.capabilities.collections === 'manual').length;
    return coverage(active.length, missing.length - unrecoverable, unrecoverable, 'pólizas vigentes', 'con cuotas pendientes de carga', 'sin cobranza informada por la compañía');
  }

  private commissionCoverage(lines: CommissionLine[]): Coverage {
    const noAgreement = lines.filter(l => lineExpected(this.db.agreements, l) === null);
    const noDetail = lines.filter(l => lineExpected(this.db.agreements, l) !== null && l.settlementOid !== null && l.settled === null);
    return coverage(lines.length, noAgreement.length, noDetail.length, 'cuotas cobradas', 'sin convenio cargado: no se calcula lo esperado', 'liquidadas sin detalle por póliza');
  }

  private claimsCoverage(claims: Claim[], today: string): Coverage {
    const open = claims.filter(c => OPEN_CLAIM.includes(c.status));
    const stale = open.filter(c => this.companyOf(c.companyOid)?.capabilities.queryClaims !== 'api'
      && daysBetween(c.lastUpdateDate, today) >= CLAIM_STALE_NEWS_DAYS).length;
    return coverage(open.length, stale, 0, 'siniestros abiertos', 'sin novedades: la compañía no se consulta por API, actualizá el estado a mano', '');
  }

  // ── Money helpers ─────────────────────────────────────────────

  private moneyTotals(lines: CommissionLine[]) {
    const settledLines = lines.filter(l => l.settlementOid !== null);
    const accruedLines = lines.filter(l => l.settlementOid === null);
    const compared = settledLines.filter(l => l.settled !== null && lineExpected(this.db.agreements, l) !== null);
    const diffOf = (l: CommissionLine) => (l.settled ?? 0) - (lineExpected(this.db.agreements, l) ?? 0);
    const detected = compared.filter(l => Math.abs(diffOf(l)) >= COMMISSION_TOLERANCE);
    const claimed = settledLines.filter(l => l.ticketOid !== null);
    return {
      settled: sum(settledLines, l => l.settled ?? 0),
      expected: sum(compared, l => lineExpected(this.db.agreements, l) ?? 0),
      accrued: sum(accruedLines, l => lineExpected(this.db.agreements, l) ?? 0),
      detected: { count: detected.length, amount: sum(detected, l => Math.abs(diffOf(l))) },
      claimed: { count: claimed.length, amount: sum(claimed, l => Math.abs(diffOf(l))) },
    };
  }

  private memberName(oid: string): string {
    return this.db.members.find(m => m.oid === oid)?.name ?? '—';
  }

  private metric(value: number, cov: Coverage, link: LinkTarget | null): Metric {
    return { value, coverage: cov, link };
  }

  // ── Dashboard (P-01) ──────────────────────────────────────────

  dashboard(f: ReportFilters): DashboardView | null {
    const ctx = this.context(f);
    if (!ctx) return null;
    const policies = this.policies(ctx);
    const emitted = policies.filter(p => this.inRange(p.startDate, ctx.range));
    const emittedBefore = policies.filter(p => this.inRange(p.startDate, ctx.previous));
    const premium = sum(emitted, p => p.premium);
    const previousPremium = sum(emittedBefore, p => p.premium);
    const emittedCoverage = fullCoverage(emitted.length, 'pólizas emitidas en el período');
    const polLink = (extra: Record<string, string> = {}, range = true) => this.link('/polizas', ctx, { range }, extra);
    const quotes = this.quotes(ctx).filter(q => this.inRange(q.createDate, ctx.range));

    const empty = this.db.policies.filter(p => ctx.owners.has(p.ownerOid)).length === 0;

    return {
      scope: this.scope(ctx), range: ctx.range, previous: ctx.previous, empty,
      production: {
        premium: {
          ...this.metric(premium, emittedCoverage, polLink()),
          previous: previousPremium, deltaPct: previousPremium === 0 ? null : pct(premium - previousPremium, previousPremium),
        },
        policies: {
          ...this.metric(emitted.length, emittedCoverage, polLink()),
          newCount: emitted.filter(p => p.renewalOfOid === null).length,
          renewalCount: emitted.filter(p => p.renewalOfOid !== null).length,
        },
        months: this.monthTrend(ctx, policies),
        monthsCoverage: fullCoverage(policies.length, 'pólizas de la cartera'),
      },
      portfolio: this.portfolio(ctx, policies),
      funnel: this.funnel(ctx, quotes),
      ranking: ctx.role === 'producer' ? this.ranking(ctx, policies) : null,
      money: ctx.role === 'producer' ? this.money(ctx, policies) : null,
      attention: this.attention(ctx, policies),
    };
  }

  private monthTrend(ctx: Ctx, policies: Policy[]): BarItem[] {
    const lastMonth = monthStart(ctx.range.to < ctx.today ? ctx.range.to : ctx.today);
    return Array.from({ length: MONTHS_IN_TREND }, (_, i) => shiftMonth(lastMonth, i - (MONTHS_IN_TREND - 1))).map(from => {
      const to = monthEnd(from);
      const inMonth = policies.filter(p => p.startDate >= from && p.startDate <= to);
      return {
        key: from.slice(0, 7), label: periodLabel(from.slice(0, 7)).split(' ')[0].slice(0, 3), value: sum(inMonth, p => p.premium),
        sub: `${inMonth.length} pólizas`,
        link: { path: '/polizas', filters: { ...this.link('/polizas', ctx, { range: false }).filters, from, to } },
      };
    });
  }

  private portfolio(ctx: Ctx, policies: Policy[]): DashboardView['portfolio'] {
    const active = policies.filter(p => p.status === 'active');
    const cov = this.portfolioCoverage(policies, ctx.today);
    const base = (extra: Record<string, string>) => this.link('/polizas', ctx, {}, { status: 'active', ...extra });
    const group = (key: (p: Policy) => string, label: (k: string) => string, filterKey: string) => {
      const map = new Map<string, { label: string; value: number; sub: string; link: LinkTarget }>();
      for (const p of active) {
        const k = key(p);
        const entry = map.get(k) ?? { label: label(k), value: 0, sub: '', link: this.link('/polizas', ctx, { line: filterKey === 'lineOfBusiness' ? null : 'lineOfBusiness', company: filterKey !== 'companyOid' }, { status: 'active', [filterKey]: k }) };
        entry.value++;
        map.set(k, entry);
      }
      for (const [k, entry] of map) entry.sub = `${sum(active.filter(p => key(p) === k), p => p.premium).toLocaleString('es-AR')} anual`;
      return bars(map);
    };
    return {
      count: this.metric(active.length, cov, base({})),
      premium: this.metric(sum(active, p => p.premium), cov, base({})),
      byLine: group(p => p.lineOfBusiness, k => LINE_LABELS[k as LineOfBusiness], 'lineOfBusiness'),
      byCompany: group(p => p.companyOid, k => this.companyOf(k)?.name ?? k, 'companyOid'),
    };
  }

  private funnel(ctx: Ctx, quotes: Quote[]): DashboardView['funnel'] {
    const stage = (key: string, label: string, count: number, extra: Record<string, string>): FunnelStage => ({
      key, label, value: count, pctOfFirst: pct(count, quotes.length),
      link: this.link('/cotizaciones', ctx, { range: true }, extra),
    });
    const reached = (key: string) => quotes.filter(q => QUOTE_STAGES[key].includes(q.status)).length;
    const desisted = quotes.filter(q => q.status === 'desisted');
    const reasons = new Map<string, { label: string; value: number; link: LinkTarget }>();
    for (const q of desisted) {
      const reason = q.desistReason ?? NO_REASON;
      const entry = reasons.get(reason) ?? { label: reason, value: 0, link: this.link('/cotizaciones', ctx, { range: true }, { status: 'desisted' }) };
      entry.value++;
      reasons.set(reason, entry);
    }
    const issued = reached('issued');
    return {
      stages: [
        stage('created', 'Creadas', quotes.length, {}),
        stage('presented', 'Presentadas', reached('presented'), { stage: 'presented' }),
        stage('accepted', 'Aceptadas', reached('accepted'), { stage: 'accepted' }),
        stage('issued', 'Emitidas', issued, { stage: 'issued' }),
      ],
      conversionPct: pct(issued, quotes.length),
      coverage: fullCoverage(quotes.length, 'cotizaciones del período'),
      desisted: desisted.length,
      reasons: bars(reasons),
      reasonsCoverage: coverage(desisted.length, desisted.filter(q => !q.desistReason).length, 0, 'cotizaciones desistidas', 'sin motivo cargado', ''),
    };
  }

  private ranking(ctx: Ctx, policies: Policy[]): DashboardView['ranking'] {
    const emitted = policies.filter(p => this.inRange(p.startDate, ctx.range));
    const map = new Map<string, { label: string; value: number; sub: string; link: LinkTarget }>();
    for (const p of emitted) {
      const entry = map.get(p.ownerOid) ?? {
        label: this.memberName(p.ownerOid), value: 0, sub: '',
        link: { path: '/polizas', filters: { ...this.link('/polizas', ctx, { range: true, member: false }).filters, ownerOid: p.ownerOid } },
      };
      entry.value += p.premium;
      map.set(p.ownerOid, entry);
    }
    for (const [oid, entry] of map) entry.sub = `${emitted.filter(p => p.ownerOid === oid).length} pólizas`;
    return { items: bars(map, TOP_ITEMS), coverage: fullCoverage(emitted.length, 'pólizas emitidas en el período') };
  }

  private money(ctx: Ctx, policies: Policy[]): DashboardView['money'] {
    const lines = this.lines(ctx, true);
    const totals = this.moneyTotals(lines);
    const lineCov = this.commissionCoverage(lines);
    const commLink = (extra: Record<string, string> = {}) => this.link('/comisiones', ctx, { range: true, line: 'line' }, extra);
    const rows = this.installments(ctx).map(({ item }) => ({ item, status: this.displayStatus(item, ctx.today) }));
    const bucket = (test: (s: string) => boolean) => {
      const hits = rows.filter(r => test(r.status));
      return { count: hits.length, amount: sum(hits, r => r.item.amount) };
    };
    const instLink = (status: string) => this.link('/cobranzas', ctx, { line: 'lineOfBusiness' }, { status });
    return {
      settled: this.metric(totals.settled, lineCov, commLink()),
      accrued: this.metric(totals.accrued, lineCov, commLink()),
      differences: { detected: totals.detected, claimed: totals.claimed, coverage: lineCov, link: commLink({ onlyDiff: 'true' }) },
      collections: {
        upToDate: bucket(s => s === 'paid' || s === 'pending'), overdue: bucket(s => s === 'overdue'), mora: bucket(s => s === 'mora'),
        coverage: this.collectionsCoverage(policies), link: instLink('overdue'),
      },
    };
  }

  private attention(ctx: Ctx, policies: Policy[]): AttentionItem[] {
    const { today } = ctx;
    const active = policies.filter(p => p.status === 'active');
    const policyCov = this.portfolioCoverage(policies, today);
    const claims = this.claims(ctx);
    const claimCov = this.claimsCoverage(claims, today);
    const myTickets = this.db.tickets.filter((t: Ticket) => t.category === 'task' && t.assigneeOid === ctx.me.oid && ACTIVE_TICKET.includes(t.status));
    const ticketCov = fullCoverage(myTickets.length, 'tickets asignados a mí');
    const unpaid = this.installments(ctx).filter(({ item }) => this.displayStatus(item, today) === 'overdue');
    const collCov = this.collectionsCoverage(policies);
    const chosen = this.quotes(ctx).filter(q => q.status === 'chosen-by-client');
    const uncontacted = chosen.filter(q => isUncontactedChoice(q, this.db));
    const item = (key: string, group: AttentionItem['group'], label: string, value: number, tone: AttentionItem['tone'], cov: Coverage, link: LinkTarget, hint: string | null = null): AttentionItem =>
      ({ key, group, label, value, tone, hint, coverage: cov, link });
    const renew = (days: number) => active.filter(p => p.endDate >= today && p.endDate <= addDays(today, days)).length;
    const polLink = (extra: Record<string, string>) => this.link('/polizas', ctx, { line: 'lineOfBusiness' }, extra);
    const mine = (extra: Record<string, string>) => ({ path: '/bandeja', filters: { view: 'assigned', ...extra } });
    const openClaims = claims.filter(c => OPEN_CLAIM.includes(c.status));
    const nearDeadline = openClaims.filter(c => c.legalDeadlineDate <= addDays(today, CLAIM_DEADLINE_WARN_DAYS));
    const stale = active.filter(p => daysBetween(p.lastSyncDate, today) > this.staleDays(p.lineOfBusiness));
    return [
      ...[30, 60, 90].map(days => item(`renew-${days}`, 'renewals', `Renovaciones en ${days} días`, renew(days), 'neutral', policyCov,
        this.link('/renovaciones', ctx, { line: 'lineOfBusiness' }, { expiring: String(days) }),
        'Si la fecha de fin podría estar desactualizada, sincronizá antes de gestionar.')),
      item('claims-open', 'claims', 'Siniestros abiertos', openClaims.length, 'neutral', claimCov,
        this.link('/siniestros', ctx, { line: 'lineOfBusiness' }, { open: 'open' })),
      item('claims-deadline', 'claims', `Con plazo legal por vencer (${CLAIM_DEADLINE_WARN_DAYS} días)`, nearDeadline.length, nearDeadline.length ? 'error' : 'neutral', claimCov,
        this.link('/siniestros', ctx, { line: 'lineOfBusiness' }, { deadline: 'true' })),
      item('tickets-overdue', 'tickets', 'Mis tickets vencidos', myTickets.filter(t => t.dueDate !== null && t.dueDate < today).length, 'error', ticketCov,
        mine({ overdue: 'true' }), 'No se filtran por compañía ni ramo: son tu carga personal.'),
      item('tickets-today', 'tickets', 'Mis tickets que vencen hoy', myTickets.filter(t => t.dueDate === today).length, 'warning', ticketCov, mine({ due: 'today' })),
      item('tickets-open', 'tickets', 'Mis tickets abiertos', myTickets.length, 'neutral', ticketCov, mine({ active: 'true' })),
      item('policies-divergent', 'policies', 'Pólizas divergentes', active.filter(p => p.divergent).length, 'warning', policyCov, polLink({ status: 'active', divergent: 'true' })),
      item('policies-stale', 'policies', 'Pólizas con sincronización vieja', stale.length, 'warning', policyCov, polLink({ status: 'active', stale: 'true' })),
      item('installments-unpaid', 'collections', 'Cuotas impagas recientes', unpaid.length, 'warning', collCov,
        this.link('/cobranzas', ctx, { line: 'lineOfBusiness' }, { status: 'overdue' }), `Vencidas hace ${MORA_DAYS} días o menos; las más viejas están en mora.`),
      item('clients-uncontacted', 'clients', 'Eligieron una opción y no los contactaste', uncontacted.length, uncontacted.length ? 'warning' : 'neutral',
        fullCoverage(chosen.length, 'cotizaciones con opción elegida'),
        this.link('/cotizaciones', ctx, { line: 'lineOfBusiness' }, { uncontacted: 'true' }),
        'Cuenta como contacto un mail enviado desde la plataforma después de la elección.'),
    ];
  }

  // ── Boards (P-75) ─────────────────────────────────────────────

  board(id: BoardId, f: ReportFilters): BoardView | null {
    const ctx = this.context(f);
    if (!ctx) return null;
    if (id === 'commissions' && ctx.role !== 'producer') return null;
    const base: BoardView = { board: id, scope: this.scope(ctx), range: ctx.range, asOf: null, kpis: [], funnel: null, months: null, groups: [], tables: [], notes: [] };
    switch (id) {
      case 'production': return { ...base, ...this.productionBoard(ctx) };
      case 'portfolio': return { ...base, ...this.portfolioBoard(ctx) };
      case 'collections': return { ...base, ...this.collectionsBoard(ctx) };
      case 'commissions': return { ...base, ...this.commissionsBoard(ctx) };
      case 'claims': return { ...base, ...this.claimsBoard(ctx) };
      default: return null;
    }
  }

  private kpi(key: string, label: string, value: number, kind: BoardKpi['kind'], sub: string | null, cov: Coverage | null, link: LinkTarget | null): BoardKpi {
    return { key, label, value, kind, sub, coverage: cov, link };
  }

  private group(key: string, title: string, kind: BarGroup['kind'], items: BarItem[], cov: Coverage | null, hint: string | null = null): BarGroup {
    return { key, title, kind, items, coverage: cov, hint };
  }

  private productionBoard(ctx: Ctx): Partial<BoardView> {
    const policies = this.policies(ctx);
    const emitted = policies.filter(p => this.inRange(p.startDate, ctx.range));
    const before = policies.filter(p => this.inRange(p.startDate, ctx.previous));
    const premium = sum(emitted, p => p.premium);
    const prevPremium = sum(before, p => p.premium);
    const cov = fullCoverage(emitted.length, 'pólizas emitidas en el período');
    const quotes = this.quotes(ctx).filter(q => this.inRange(q.createDate, ctx.range));
    const funnel = this.funnel(ctx, quotes);
    const polLink = (extra: Record<string, string> = {}, opts: { company?: boolean; line?: 'lineOfBusiness' | null } = {}) =>
      this.link('/polizas', ctx, { range: true, ...opts }, extra);
    const groupBy = (key: (p: Policy) => string, label: (k: string) => string, filterKey: 'companyOid' | 'lineOfBusiness') => {
      const map = new Map<string, { label: string; value: number; sub: string; link: LinkTarget }>();
      for (const p of emitted) {
        const k = key(p);
        const entry = map.get(k) ?? { label: label(k), value: 0, sub: '', link: polLink({ [filterKey]: k }, filterKey === 'companyOid' ? { company: false } : { line: null }) };
        entry.value += p.premium;
        map.set(k, entry);
      }
      for (const [k, entry] of map) entry.sub = `${emitted.filter(p => key(p) === k).length} pólizas`;
      return bars(map);
    };
    const kinds = new Map<string, { label: string; value: number; link: LinkTarget }>([
      ['new', { label: 'Nuevas', value: emitted.filter(p => p.renewalOfOid === null).length, link: polLink({ renewal: 'no' }) }],
      ['renewal', { label: 'Renovaciones', value: emitted.filter(p => p.renewalOfOid !== null).length, link: polLink({ renewal: 'yes' }) }],
    ]);
    const groups = [
      this.group('company', 'Prima emitida por compañía', 'money', groupBy(p => p.companyOid, k => this.companyOf(k)?.name ?? k, 'companyOid'), cov),
      this.group('line', 'Prima emitida por ramo', 'money', groupBy(p => p.lineOfBusiness, k => LINE_LABELS[k as LineOfBusiness], 'lineOfBusiness'), cov),
      this.group('kind', 'Nuevas contra renovaciones (pólizas)', 'number', bars(kinds), cov),
    ];
    const ranking = ctx.role === 'producer' ? this.ranking(ctx, policies) : null;
    if (ranking) {
      groups.push(this.group('producer', 'Producción por productor y vendedor', 'money', ranking.items, ranking.coverage,
        'Muestra producción, no el reparto de comisión.'));
    }
    groups.push(this.group('reasons', 'Motivos de pérdida (cotizaciones desistidas)', 'number', funnel.reasons, funnel.reasonsCoverage));
    return {
      kpis: [
        this.kpi('premium', 'Prima emitida', premium, 'money',
          prevPremium === 0 ? `Sin emisión en ${ctx.previous.label}` : `${premium >= prevPremium ? '+' : ''}${pct(premium - prevPremium, prevPremium)} % contra ${ctx.previous.label}`, cov, polLink()),
        this.kpi('policies', 'Pólizas emitidas', emitted.length, 'number',
          `${emitted.filter(p => p.renewalOfOid === null).length} nuevas · ${emitted.filter(p => p.renewalOfOid !== null).length} renovaciones`, cov, polLink()),
        this.kpi('quotes', 'Cotizaciones creadas', quotes.length, 'number', null, funnel.coverage, this.link('/cotizaciones', ctx, { range: true })),
        this.kpi('conversion', 'Conversión de cotizaciones', funnel.conversionPct, 'percent', 'emitidas sobre creadas', funnel.coverage, this.link('/cotizaciones', ctx, { range: true }, { stage: 'issued' })),
      ],
      funnel: { stages: funnel.stages, coverage: funnel.coverage },
      months: { title: 'Prima emitida por mes', kind: 'money', items: this.monthTrend(ctx, policies) },
      groups,
      notes: ['La apertura por productor muestra producción (prima emitida), no el reparto de comisión.'],
    };
  }

  private activeAt(p: Policy, date: string, today: string): boolean {
    if (date === today) return p.status === 'active';
    if (p.startDate > date) return false;
    const version = [...p.versions].filter(v => v.effectDate <= date).sort((a, b) => b.version - a.version)[0];
    return version?.status !== 'cancelled' && date <= (version?.endDate ?? p.endDate);
  }

  private portfolioBoard(ctx: Ctx): Partial<BoardView> {
    const requested = ctx.filters.asOf && /^\d{4}-\d{2}-\d{2}$/.test(ctx.filters.asOf) ? ctx.filters.asOf : '';
    const asOf = requested || (ctx.range.to < ctx.today ? ctx.range.to : ctx.today);
    const live = asOf === ctx.today;
    const policies = this.policies(ctx);
    const active = policies.filter(p => this.activeAt(p, asOf, ctx.today));
    const premiumAt = (p: Policy) => [...p.versions].filter(v => v.effectDate <= asOf).sort((a, b) => b.version - a.version)[0]?.premium ?? p.premium;
    const cov = live ? this.portfolioCoverage(policies, ctx.today) : fullCoverage(active.length, 'pólizas según sus versiones registradas');
    const polLink = (extra: Record<string, string>, opts: { company?: boolean; line?: 'lineOfBusiness' | null } = {}) =>
      live ? this.link('/polizas', ctx, opts, { status: 'active', ...extra }) : null;
    const groupBy = (key: (p: Policy) => string, label: (k: string) => string, filterKey: 'companyOid' | 'lineOfBusiness') => {
      const map = new Map<string, { label: string; value: number; sub: string; link: LinkTarget | null }>();
      for (const p of active) {
        const k = key(p);
        const entry = map.get(k) ?? { label: label(k), value: 0, sub: '', link: polLink({ [filterKey]: k }, filterKey === 'companyOid' ? { company: false } : { line: null }) };
        entry.value++;
        map.set(k, entry);
      }
      for (const [k, entry] of map) entry.sub = `${sum(active.filter(p => key(p) === k), premiumAt).toLocaleString('es-AR')} anual`;
      return bars(map);
    };
    const expiring = (days: number) => active.filter(p => p.endDate >= asOf && p.endDate <= addDays(asOf, days)).length;
    const buckets = new Map<string, { label: string; value: number; link: LinkTarget | null }>(
      [30, 60, 90].map(d => [String(d), { label: `En ${d} días`, value: expiring(d), link: polLink({ expiring: String(d) }) }]));
    const sorted = [...buckets.entries()].map(([key, v]) => ({ key, label: v.label, value: v.value, sub: null, link: v.link }));
    return {
      asOf,
      kpis: [
        this.kpi('count', `Cartera vigente al ${fmtDate(asOf)}`, active.length, 'number', null, cov, polLink({})),
        this.kpi('premium', 'Prima anualizada', sum(active, premiumAt), 'money', null, cov, polLink({})),
        this.kpi('expiring', 'Vencen en 30 días', expiring(30), 'number', null, cov, polLink({ expiring: '30' })),
      ],
      groups: [
        this.group('line', 'Pólizas vigentes por ramo', 'number', groupBy(p => p.lineOfBusiness, k => LINE_LABELS[k as LineOfBusiness], 'lineOfBusiness'), cov),
        this.group('company', 'Pólizas vigentes por compañía', 'number', groupBy(p => p.companyOid, k => this.companyOf(k)?.name ?? k, 'companyOid'), cov),
        this.group('expiring', 'Vencimientos próximos', 'number', sorted, cov, 'Acumulado: las de 30 días están incluidas en las de 60 y 90.'),
      ],
      notes: live ? [] : ['A una fecha anterior la cartera se arma con las versiones registradas de cada póliza; los enlaces a listados se ofrecen solo para la cartera de hoy.'],
    };
  }

  private collectionsBoard(ctx: Ctx): Partial<BoardView> {
    const policies = this.policies(ctx);
    const rows = this.installments(ctx)
      .filter(({ item }) => this.inRange(item.dueDate, ctx.range))
      .map(({ item, policy }) => ({ item, policy, status: this.displayStatus(item, ctx.today) }));
    const cov = this.collectionsCoverage(policies);
    const bucket = (test: (s: string) => boolean, list = rows) => {
      const hits = list.filter(r => test(r.status));
      return { count: hits.length, amount: sum(hits, r => r.item.amount) };
    };
    const onTime = bucket(s => s === 'paid' || s === 'pending');
    const overdue = bucket(s => s === 'overdue');
    const mora = bucket(s => s === 'mora');
    const link = (status: string) => this.link('/cobranzas', ctx, { range: true, line: 'lineOfBusiness' }, { status });
    const table = (key: string, title: string, headers: string[], keyOf: (r: (typeof rows)[number]) => string, labelOf: (k: string) => string, rowLink: (k: string) => unknown[] | null): BoardTable => {
      const keys = [...new Set(rows.map(keyOf))];
      const cells = keys.map(k => {
        const mine = rows.filter(r => keyOf(r) === k);
        const o = bucket(s => s === 'overdue', mine);
        const m = bucket(s => s === 'mora', mine);
        return { cells: [labelOf(k), bucket(s => s === 'paid' || s === 'pending', mine).count, o.count, m.count, o.amount + m.amount], link: rowLink(k) };
      }).sort((a, b) => Number(b.cells[4]) - Number(a.cells[4]));
      return { key, title, headers, moneyColumns: [4], rows: cells, coverage: cov, hint: null };
    };
    const headers = ['', 'Al día', 'Vencidas', 'En mora', 'Importe vencido y en mora'];
    const tables = [
      table('company', 'Por compañía', ['Compañía', ...headers.slice(1)], r => r.policy.companyOid, k => this.companyOf(k)?.name ?? k, () => null),
    ];
    if (ctx.role === 'producer') {
      tables.push(table('producer', 'Por productor y vendedor', ['Productor o vendedor', ...headers.slice(1)], r => r.item.ownerOid, k => this.memberName(k), () => null));
    }
    const arrears = new Map<string, { label: string; value: number; link: LinkTarget }>();
    for (const r of rows.filter(x => x.status === 'overdue' || x.status === 'mora')) {
      const entry = arrears.get(r.policy.companyOid) ?? {
        label: this.companyOf(r.policy.companyOid)?.name ?? r.policy.companyOid, value: 0,
        link: this.link('/cobranzas', ctx, { range: true, line: 'lineOfBusiness', company: false }, { companyOid: r.policy.companyOid }),
      };
      entry.value += r.item.amount;
      arrears.set(r.policy.companyOid, entry);
    }
    return {
      kpis: [
        this.kpi('on-time', 'Cuotas al día', onTime.count, 'number', `${onTime.amount.toLocaleString('es-AR')} en pesos`, cov, link('on-time')),
        this.kpi('overdue', 'Cuotas vencidas', overdue.count, 'number', `${overdue.amount.toLocaleString('es-AR')} en pesos`, cov, link('overdue')),
        this.kpi('mora', `Cuotas en mora (más de ${MORA_DAYS} días)`, mora.count, 'number', `${mora.amount.toLocaleString('es-AR')} en pesos`, cov, link('mora')),
      ],
      groups: [this.group('arrears', 'Importe vencido y en mora por compañía', 'money', bars(arrears), cov)],
      tables,
      notes: ['Se consideran las cuotas con vencimiento dentro del período elegido; el estado es el de hoy.'],
    };
  }

  private commissionsBoard(ctx: Ctx): Partial<BoardView> {
    const lines = this.lines(ctx, true);
    const totals = this.moneyTotals(lines);
    const cov = this.commissionCoverage(lines);
    const link = (extra: Record<string, string> = {}) => this.link('/comisiones', ctx, { range: true, line: 'line' }, extra);
    const companies = [...new Set(lines.map(l => l.companyOid))];
    const rows = companies.map(oid => {
      const t = this.moneyTotals(lines.filter(l => l.companyOid === oid));
      return { cells: [this.companyOf(oid)?.name ?? oid, t.expected, t.settled, t.settled - t.expected, t.accrued, t.claimed.count], link: null };
    }).sort((a, b) => Number(b.cells[2]) - Number(a.cells[2]));
    const settledBy = new Map<string, { label: string; value: number; link: LinkTarget }>();
    for (const oid of companies) {
      settledBy.set(oid, {
        label: this.companyOf(oid)?.name ?? oid, value: this.moneyTotals(lines.filter(l => l.companyOid === oid)).settled,
        link: this.link('/comisiones', ctx, { range: true, line: 'line', company: false }, { companyOid: oid }),
      });
    }
    return {
      kpis: [
        this.kpi('settled', 'Liquidado', totals.settled, 'money', 'lo que informó la compañía', cov, link()),
        this.kpi('expected', 'Esperado', totals.expected, 'money', 'convenio por prima cobrada, de lo liquidado', cov, link()),
        this.kpi('accrued', 'Devengado no liquidado', totals.accrued, 'money', 'cuotas cobradas que la compañía no liquidó', cov, link()),
        this.kpi('claimed', 'Diferencias reclamadas', totals.claimed.count, 'number', `${totals.claimed.amount.toLocaleString('es-AR')} en pesos; detectadas: ${totals.detected.count}`, cov, link({ onlyDiff: 'true' })),
      ],
      groups: [this.group('company', 'Liquidado por compañía', 'money', bars(settledBy), cov)],
      tables: [{
        key: 'company', title: 'Por compañía', headers: ['Compañía', 'Esperado', 'Liquidado', 'Diferencia', 'Devengado no liquidado', 'Reclamos'],
        moneyColumns: [1, 2, 3, 4], rows, coverage: cov, hint: null,
      }],
      notes: ['Solo el productor ve comisiones. Para el reparto con cada hijo, ver Convenios y reparto.'],
    };
  }

  private claimsBoard(ctx: Ctx): Partial<BoardView> {
    const all = this.claims(ctx);
    const inPeriod = all.filter(c => this.inRange(c.reportedDate, ctx.range));
    const open = all.filter(c => OPEN_CLAIM.includes(c.status));
    const near = open.filter(c => c.legalDeadlineDate <= addDays(ctx.today, CLAIM_DEADLINE_WARN_DAYS));
    const cov = this.claimsCoverage(all, ctx.today);
    const periodCov = fullCoverage(inPeriod.length, 'siniestros del período');
    const lineOf = new Map(this.db.policies.map(p => [p.oid, p.lineOfBusiness]));
    const link = (extra: Record<string, string> = {}, range = true) => this.link('/siniestros', ctx, { range, line: 'lineOfBusiness' }, extra);
    const groupBy = (key: (c: Claim) => string, label: (k: string) => string, filterKey: 'status' | 'lineOfBusiness' | 'companyOid') => {
      const map = new Map<string, { label: string; value: number; link: LinkTarget }>();
      for (const c of inPeriod) {
        const k = key(c);
        const entry = map.get(k) ?? {
          label: label(k), value: 0,
          link: this.link('/siniestros', ctx, { range: true, line: filterKey === 'lineOfBusiness' ? null : 'lineOfBusiness', company: filterKey !== 'companyOid' }, { [filterKey]: k }),
        };
        entry.value++;
        map.set(k, entry);
      }
      return bars(map);
    };
    const statusLabels: Record<string, string> = {
      reported: 'Denunciado', documentation: 'Documentación', 'in-analysis': 'En análisis', accepted: 'Aceptado', rejected: 'Rechazado', closed: 'Cerrado',
    };
    const byPerson = new Map<string, { claims: number; amount: number }>();
    for (const c of inPeriod) {
      const entry = byPerson.get(c.personOid) ?? { claims: 0, amount: 0 };
      entry.claims++;
      entry.amount += c.settledAmount ?? 0;
      byPerson.set(c.personOid, entry);
    }
    const personRows = [...byPerson.entries()].sort((a, b) => b[1].claims - a[1].claims || b[1].amount - a[1].amount).slice(0, TOP_ITEMS).map(([oid, v]) => {
      const policies = this.db.policies.filter(p => p.personOid === oid && p.status === 'active' && ctx.owners.has(p.ownerOid)).length;
      return {
        cells: [this.db.persons.find(p => p.oid === oid)?.displayName ?? '—', v.claims, policies, v.amount, policies ? Math.round((v.claims / policies) * 100) / 100 : 0],
        link: ['/clientes', oid] as unknown[],
      };
    });
    const personName = (oid: string) => this.db.persons.find(p => p.oid === oid)?.displayName ?? '—';
    return {
      kpis: [
        this.kpi('period', 'Siniestros del período', inPeriod.length, 'number', null, periodCov, link()),
        this.kpi('open', 'Abiertos hoy', open.length, 'number', null, cov, link({ open: 'open' }, false)),
        this.kpi('deadline', `Con plazo legal por vencer (${CLAIM_DEADLINE_WARN_DAYS} días)`, near.length, 'number', null, cov, link({ deadline: 'true' }, false)),
        this.kpi('amount', 'Importe liquidado del período', sum(inPeriod, c => c.settledAmount ?? 0), 'money',
          `${inPeriod.filter(c => c.settledAmount !== null).length} con importe informado`, periodCov, link()),
      ],
      groups: [
        this.group('status', 'Siniestros por estado', 'number', groupBy(c => c.status, k => statusLabels[k] ?? k, 'status'), periodCov),
        this.group('line', 'Siniestros por ramo', 'number', groupBy(c => lineOf.get(c.policyOid) ?? 'auto', k => LINE_LABELS[k as LineOfBusiness], 'lineOfBusiness'), periodCov),
        this.group('company', 'Siniestros por compañía', 'number', groupBy(c => c.companyOid, k => this.companyOf(k)?.name ?? k, 'companyOid'), periodCov),
      ],
      tables: [
        {
          key: 'deadlines', title: 'Abiertos con plazos por vencer', headers: ['Siniestro', 'Cliente', 'Compañía', 'Plazo legal'], moneyColumns: [],
          rows: near.sort((a, b) => a.legalDeadlineDate.localeCompare(b.legalDeadlineDate)).slice(0, 10).map(c => ({
            cells: [c.number, personName(c.personOid), this.companyOf(c.companyOid)?.name ?? '—', fmtDate(c.legalDeadlineDate)], link: ['/siniestros', c.oid] as unknown[],
          })),
          coverage: cov, hint: null,
        },
        {
          key: 'clients', title: 'Siniestralidad por cliente', headers: ['Cliente', 'Siniestros', 'Pólizas vigentes', 'Importe liquidado', 'Siniestros por póliza'], moneyColumns: [3],
          rows: personRows, coverage: periodCov, hint: 'Clientes con más siniestros en el período.',
        },
      ],
    };
  }
}
